import { useEffect, useState } from "react";
import { Navbar } from "@/components/Navbar";
import api from "@/lib/api";
import { StatusPill } from "@/pages/CookDashboard";
import { toast } from "sonner";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [tx, setTx] = useState([]);
  const [trends, setTrends] = useState(null);
  const [trendState, setTrendState] = useState("");

  const loadTrends = async (st) => {
    try {
      const { data } = await api.get("/trends/amenities", { params: st ? { state: st } : {} });
      setTrends(data);
    } catch (err) { console.error("Trends load:", err); }
  };

  useEffect(() => {
    api.get("/admin/stats").then(r => setStats(r.data));
    api.get("/admin/transactions").then(r => setTx(r.data));
    loadTrends("");
  }, []);

  const backfill = async () => {
    try {
      const { data } = await api.post("/admin/backfill-states");
      toast.success(`Filled state on ${data.filled}/${data.checked} kitchens`);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-14">
        <div className="tick-label">Admin</div>
        <h1 className="font-display font-black text-5xl tracking-tighter mt-2">Platform earnings.</h1>

        {stats && (
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 border hairline">
            <Stat label="Gross volume" value={`$${stats.gross_volume.toFixed(2)}`} />
            <Stat label="Platform fees (15%)" value={`$${stats.platform_fees.toFixed(2)}`} highlight />
            <Stat label="Users" value={stats.total_users} />
            <Stat label="Kitchens" value={stats.total_kitchens} />
          </div>
        )}

        <div className="mt-10 flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="tick-label">Regional amenity trends</div>
            <div className="font-display text-2xl font-bold mt-1">What hosts equip {trends?.state || "everywhere"}</div>
          </div>
          <div className="flex gap-2">
            <input
              data-testid="trends-state-input"
              value={trendState}
              onChange={(e) => setTrendState(e.target.value.toUpperCase())}
              placeholder="State (CA, NY…)"
              className="border hairline px-3 py-2 uppercase focus:outline-none focus:border-[#FF3B30]"
              maxLength={2}
            />
            <button data-testid="trends-apply" onClick={() => loadTrends(trendState)} className="btn-signal">Apply</button>
            <button data-testid="admin-backfill-btn" onClick={backfill} className="btn-outline">Backfill states</button>
          </div>
        </div>
        {trends && trends.top.length > 0 && (
          <div data-testid="trends-list" className="mt-4 border hairline p-4">
            <div className="tick-label mb-3">{trends.total_kitchens} kitchens · top {trends.top.length} amenities</div>
            <div className="space-y-2">
              {trends.top.map((row) => {
                const pct = trends.total_kitchens ? Math.round(row.count / trends.total_kitchens * 100) : 0;
                return (
                  <div key={row.amenity} className="flex items-center gap-3">
                    <div className="w-40 text-sm truncate">{row.amenity}</div>
                    <div className="flex-1 bg-neutral-900 border hairline h-5 overflow-hidden">
                      <div className="h-full bg-[#FF3B30]" style={{ width: `${pct}%` }} />
                    </div>
                    <div className="font-mono text-sm w-20 text-right">{row.count} · {pct}%</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="mt-14">
          <div className="tick-label mb-4">Transactions</div>
          {tx.length === 0 ? (
            <div className="border hairline p-10 text-center text-neutral-500">No transactions.</div>
          ) : (
            <div className="border hairline overflow-x-auto">
              <table className="w-full">
                <thead className="border-b hairline">
                  <tr className="text-left tick-label">
                    <th className="p-4">When</th>
                    <th className="p-4">Session</th>
                    <th className="p-4">Booking</th>
                    <th className="p-4">Amount</th>
                    <th className="p-4">Platform fee</th>
                    <th className="p-4">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {tx.map(t => (
                    <tr key={t.session_id} data-testid={`tx-${t.session_id}`} className="border-b hairline font-mono text-sm">
                      <td className="p-4">{new Date(t.created_at).toLocaleString()}</td>
                      <td className="p-4 truncate max-w-[180px]">{t.session_id}</td>
                      <td className="p-4 truncate max-w-[140px]">{t.booking_id}</td>
                      <td className="p-4">${t.amount.toFixed(2)}</td>
                      <td className="p-4 text-[#FF3B30]">${t.platform_fee?.toFixed(2) || "0.00"}</td>
                      <td className="p-4"><StatusPill s={t.payment_status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div className="p-6 border-r hairline last:border-r-0">
      <div className="tick-label">{label}</div>
      <div className={`font-mono text-3xl font-bold mt-2 ${highlight ? "text-[#FF3B30]" : ""}`}>{value}</div>
    </div>
  );
}
